import { z } from 'zod/v4';

export const MeasurementStatusRuntype = z.enum(['active', 'ignored']);

export type MeasurementStatus = z.infer<typeof MeasurementStatusRuntype>;

// Renderer service types for trace width and distance analysis
export const TraceWidthDescriptionRuntype = z.object({
    id: z.string(),
    status: MeasurementStatusRuntype,
    element: z.number(), // SVG element ID
    width: z.number(), // trace width in micrometers
    distance: z
        .object({
            start: z.object({ x: z.number(), y: z.number() }),
            end: z.object({ x: z.number(), y: z.number() }),
        })
        .optional(),
    trace: z.string().optional(),
    netId: z.string().optional(),
});

export type TraceWidthDescription = z.infer<typeof TraceWidthDescriptionRuntype>;

export const DistanceDescriptionRuntype = z.object({
    id: z.string(),
    status: MeasurementStatusRuntype,
    from: z.number().nullable(),
    to: z.number().nullable(),
    length: z.number(),
    distance: z.object({
        start: z.object({ x: z.number(), y: z.number() }),
        end: z.object({ x: z.number(), y: z.number() }),
    }),
    fromTrace: z.string().optional(),
    toTrace: z.string().optional(),
    fromNet: z.string().optional(),
    toNet: z.string().optional(),
    isSameNet: z.boolean().optional(),
});

export type DistanceDescription = z.infer<typeof DistanceDescriptionRuntype>;

const PointRuntype = z.object({ x: z.number(), y: z.number() });

const DrillTypeRuntype = z.enum(['PLATED', 'NON_PLATED', 'UNKNOWN']);

// Annular ring measurements from the renderer service. The renderer returns raw internal units
// (1/100 mm) for `size` and the `line` coordinates, exactly like trace width / distance. `size`
// can be negative (breakout). The response is a flat { rings: [...] } object, not grouped by file.
export const AnnularRingDescriptionRuntype = z.object({
    id: z.string(),
    status: MeasurementStatusRuntype,
    t: z.object({
        name: z.string(),
        drillType: DrillTypeRuntype,
        diameter: z.number(),
    }),
    layer: z.number(),
    ringType: z.enum(['inside', 'breakout', 'tangential']),
    hit: PointRuntype,
    size: z.number(), // raw 1/100 mm, may be negative
    line: z.object({
        start: PointRuntype,
        end: PointRuntype,
        supp: z.array(PointRuntype).optional(),
        t: z.string().optional(),
    }),
    file: z.string(),
});

export type AnnularRingDescription = z.infer<typeof AnnularRingDescriptionRuntype>;

export const AnnularRingsDescriptionRuntype = z.object({
    rings: z.array(AnnularRingDescriptionRuntype),
});

export type AnnularRingsDescription = z.infer<typeof AnnularRingsDescriptionRuntype>;

// Reconciled drill data from PCBService.getDrills (Seq[HoleList]). Hit positions and tool diameters
// are in the renderer's internal coordinate space (the same space the DFM canvas and the annular-ring
// overlay use), so a drill is a circle at (x, y) with radius diameter/2 — no scaling. Only the fields
// needed for drawing are modelled; HoleList.scaling is intentionally ignored.
export const DrillToolRuntype = z.object({
    name: z.string(),
    drillType: DrillTypeRuntype,
    diameter: z.number(),
    drills: z.array(PointRuntype),
});

export type DrillTool = z.infer<typeof DrillToolRuntype>;

export const HoleListRuntype = z.object({
    from: z.number().nullable().optional(),
    to: z.number().nullable().optional(),
    tools: z.array(DrillToolRuntype),
    scaling: z.number().nullable().optional(),
    fileUnit: z.string().nullable().optional(),
});

export type HoleList = z.infer<typeof HoleListRuntype>;
